import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, Empty, Select } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Pencil, KeyRound, Check, X, Mail, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const ROLES = [
  { value: "warehouse_manager", label: "Warehouse Manager" },
  { value: "regional_manager", label: "Regional Warehouse Manager" },
  { value: "operations_controller", label: "Operations Controller" },
  { value: "finance", label: "Finance" },
  { value: "fitment_technician", label: "Fitment Centre / Technician" },
  { value: "system_admin", label: "System Administrator" },
];

const REGIONS = ["Gauteng", "KwaZulu-Natal", "Western Cape", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape", "National"];

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "", region: "", site_id: "" });
  const [inviteOnCreate, setInviteOnCreate] = useState(true);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({});
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null); // {email, link, emailed}
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setSites(r.data.filter((l) => ["fitment_centre", "technician"].includes(l.type))));
  }, [load]);

  const create = async () => {
    if (!form.name || !form.email || !form.role) { toast.error("Name, email and role are required"); return; }
    if (!inviteOnCreate && !form.password) { toast.error("Password is required (or switch on 'Send invite')"); return; }
    try {
      const { data } = await api.post("/users", { ...form, send_invite: inviteOnCreate });
      setForm({ name: "", email: "", password: "", role: "", region: "", site_id: "" });
      load();
      if (data.invited) {
        setInviteInfo({ email: data.email, link: data.invite_link, emailed: data.emailed });
        toast.success(data.emailed ? `Invite emailed to ${data.email}` : `User created — invite email could not be sent`);
      } else {
        toast.success("User created");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendInvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/invite`, {});
      setInviteInfo({ email: data.email, link: data.invite_link, emailed: data.emailed });
      toast.success(data.emailed ? `Invite emailed to ${data.email}` : `Invite created — email could not be sent, copy the link`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyLink = () => {
    if (!inviteInfo?.link) return;
    navigator.clipboard.writeText(inviteInfo.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setEdit({ name: u.name || "", email: u.email || "", role: u.role || "", region: u.region || "", site_id: u.site_id || "" });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/users/${editId}`, edit);
      toast.success("User updated");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const resetPassword = async () => {
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { password: newPw });
      toast.success(`Password reset for ${pwUser.email}`);
      setPwUser(null); setNewPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const label = (r) => ROLES.find((x) => x.value === r)?.label || r;
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage platform users, their least-privilege roles, region and fitment centre." />

      <Panel title="Add User" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div><Label className="overline text-slate-500">Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="overline text-slate-500">Email</Label><Input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1" /></div>
          <div>
            <Label className="overline text-slate-500">Password</Label>
            <Input data-testid="user-password" value={form.password} disabled={inviteOnCreate} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={inviteOnCreate ? "Set via invite" : ""} className="rounded-sm mt-1 disabled:bg-slate-100 disabled:text-slate-400" />
          </div>
          <div><Label className="overline text-slate-500">Role</Label><div className="mt-1"><Select testid="user-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} placeholder="Role" /></div></div>
          <div><Label className="overline text-slate-500">Region</Label><div className="mt-1"><Select testid="user-region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} options={REGIONS.map((r) => ({ value: r, label: r }))} placeholder="—" /></div></div>
          <div>
            <Label className="overline text-slate-500">Fitment centre</Label>
            <div className="mt-1"><Select testid="user-site" value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="—" /></div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="invite-on-create-label">
            <Checkbox data-testid="invite-on-create" checked={inviteOnCreate} onCheckedChange={(v) => setInviteOnCreate(!!v)} />
            <span className="text-sm text-slate-600">Email a <span className="font-medium text-[#0B2545]">set-password invite</span> (no password needed — the user sets their own, link expires in 48h)</span>
          </label>
          <Button data-testid="user-create" onClick={create} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head shrink-0 px-8">Add User</Button>
        </div>
      </Panel>

      <Panel title="Users">
        {users.length === 0 ? <Empty>No users.</Empty> : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Region</th><th className="py-2 pr-3">Fitment centre</th><th className="py-2 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((u) => (
                editId === u.id ? (
                  <tr key={u.id} className="border-b border-slate-100 bg-[#f0fdfa]">
                    <td className="py-2 pr-3"><Input data-testid={`edit-name-${u.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="rounded-sm h-8" /></td>
                    <td className="py-2 pr-3"><Input data-testid={`edit-email-${u.id}`} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="rounded-sm h-8" /></td>
                    <td className="py-2 pr-3"><Select testid={`edit-role-${u.id}`} value={edit.role} onChange={(v) => setEdit({ ...edit, role: v })} options={ROLES} placeholder="Role" /></td>
                    <td className="py-2 pr-3"><Select testid={`edit-region-${u.id}`} value={edit.region} onChange={(v) => setEdit({ ...edit, region: v })} options={REGIONS.map((r) => ({ value: r, label: r }))} placeholder="—" /></td>
                    <td className="py-2 pr-3"><Select testid={`edit-site-${u.id}`} value={edit.site_id} onChange={(v) => setEdit({ ...edit, site_id: v })} options={sites.map((s) => ({ value: s.id, label: s.name }))} placeholder="—" /></td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button data-testid={`user-save-${u.id}`} onClick={saveEdit} title="Save" className="text-[#00857C] hover:text-[#00a89d] mr-3"><Check className="h-4 w-4 inline" /></button>
                      <button data-testid={`user-cancel-${u.id}`} onClick={() => setEditId(null)} title="Cancel" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4 inline" /></button>
                    </td>
                  </tr>
                ) : (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-[#0B2545]">{u.name}</td>
                    <td className="py-2 pr-3 text-slate-600">
                      {u.email}
                      {u.invite_pending && <span data-testid={`invite-pending-${u.id}`} className="ml-2 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide" style={{ background: "#fff7ed", color: "#b45309" }}>invite pending</span>}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">{label(u.role)}</td>
                    <td className="py-2 pr-3 text-slate-500">{u.region || "—"}</td>
                    <td className="py-2 pr-3 text-slate-500">{u.site_id ? siteName(u.site_id) : "—"}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button data-testid={`user-invite-${u.id}`} onClick={() => sendInvite(u)} title={u.invite_pending ? "Resend login invite" : "Send login invite"} className="text-slate-400 hover:text-[#00857C] mr-3"><Mail className="h-4 w-4 inline" /></button>
                      <button data-testid={`user-edit-${u.id}`} onClick={() => startEdit(u)} title="Edit details" className="text-slate-400 hover:text-[#00857C] mr-3"><Pencil className="h-4 w-4 inline" /></button>
                      <button data-testid={`user-pw-${u.id}`} onClick={() => { setPwUser(u); setNewPw(""); }} title="Reset password" className="text-slate-400 hover:text-[#b45309] mr-3"><KeyRound className="h-4 w-4 inline" /></button>
                      {u.id !== user.id && <button data-testid={`user-del-${u.id}`} onClick={() => del(u.id)} title="Delete" className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4 inline" /></button>}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent data-testid="reset-password-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-head text-[#0B2545]">Reset password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Set a new password for <span className="font-medium text-[#0B2545]">{pwUser?.email}</span>. Minimum 8 characters.</p>
          <Input data-testid="reset-password-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="rounded-sm" />
          <div className="flex justify-end gap-2">
            <Button data-testid="reset-password-cancel" variant="outline" onClick={() => setPwUser(null)} className="rounded-sm font-head">Cancel</Button>
            <Button data-testid="reset-password-confirm" onClick={resetPassword} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Reset password</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent data-testid="invite-link-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-head text-[#0B2545]">Login invite</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 -mt-2">
            {inviteInfo?.emailed
              ? <>A set-password invite was emailed to <span className="font-medium text-[#0B2545]">{inviteInfo?.email}</span>. You can also share the link below. It expires in 48 hours.</>
              : <>We couldn't send the email to <span className="font-medium text-[#0B2545]">{inviteInfo?.email}</span>, but the invite is ready — copy the link below and share it directly. It expires in 48 hours.</>}
          </p>
          <div className="flex items-center gap-2">
            <Input data-testid="invite-link-input" readOnly value={inviteInfo?.link || ""} className="rounded-sm text-xs font-label" onFocus={(e) => e.target.select()} />
            <Button data-testid="invite-link-copy" onClick={copyLink} variant="outline" className="rounded-sm font-head shrink-0 border-[#00857C] text-[#00857C] hover:bg-[#00857C] hover:text-white">
              <Copy className="h-4 w-4 mr-1" /> {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button data-testid="invite-link-done" onClick={() => setInviteInfo(null)} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
